import { useState, useEffect } from 'react';
import { Search, Send, Check, Link as LinkIcon, Copy } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { useMessaging } from './MessagingContext';
import { useUserData } from './UserDataContext';
import { generateInviteCode, buildGroupInviteLink, GROUP_COLLECTION, type GroupKind } from '../utils/inviteCode';

export interface ShareableGroup {
    id: string;
    name: string;
    type: GroupKind;
    bannerUrl?: string;
    /** false marks an invite-only group; the receiver's join path differs. */
    isPublic?: boolean;
    /** Existing code, if the group already has one. */
    inviteCode?: string;
}

interface ShareGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    group: ShareableGroup;
}

/**
 * Share a tribe or forum: to friends inside the app, or as a link for anywhere
 * else.
 *
 * The in-app half sends a groupInvite message, the same card SendGroupInviteModal
 * sends from the owner settings block. This one is reachable from the group
 * header by any viewer, and it pairs the friend list with a copyable link, which
 * is the part the owner-only modal has no answer for.
 *
 * The link half mints the group's invite code on first use. Any member can do
 * that now; it used to be owner-only, which meant the people most likely to want
 * to share a group were the ones who could not. The rules permit writing
 * inviteCode and nothing else, and only when the group has none — so a member
 * can create the first code but nobody can rotate an existing one and silently
 * break links already sent.
 */
export function ShareGroupModal({ isOpen, onClose, group }: ShareGroupModalProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
    const [copying, setCopying] = useState(false);
    const [copied, setCopied] = useState(false);
    const { addMessage } = useMessaging();
    const { friends } = useUserData();

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setSelectedFriends([]);
            setMessage('');
            setIsSending(false);
            setCopied(false);
        }
    }, [isOpen]);

    const filteredFriends = friends.filter(friend =>
        friend.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        friend.username?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleFriendSelection = (friendId: string) => {
        setSelectedFriends(prev =>
            prev.includes(friendId) ? prev.filter(id => id !== friendId) : [...prev, friendId]
        );
    };

    const label = group.type === 'tribe' ? 'Tribe' : 'Forum';

    const handleCopyLink = async () => {
        setCopying(true);
        try {
            let code = group.inviteCode;
            if (!code) {
                const { getDbSafe } = await import('../services/firebaseConfig');
                const { doc, getDoc, updateDoc } = await import('firebase/firestore');
                const db = await getDbSafe();
                const ref = doc(db, GROUP_COLLECTION[group.type], group.id);

                // Read before minting. The caller's copy of the group may not
                // carry inviteCode at all (the forum screen didn't hydrate it
                // until recently), and the rules reject a second write to a
                // field that already exists — so guessing "no code" from a stale
                // prop would fail the write on exactly the groups that already
                // have a perfectly good link.
                const snap = await getDoc(ref);
                code = snap.exists() ? (snap.data() as any)?.inviteCode : undefined;

                if (!code) {
                    code = generateInviteCode();
                    // inviteCode only — the rules reject an update that touches
                    // anything else, which is what makes this safe to expose to a
                    // member rather than only the owner.
                    await updateDoc(ref, { inviteCode: code });
                }
            }
            const link = buildGroupInviteLink(group.type, group.id, code);
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success('Link copied');
        } catch (e) {
            console.error('[ShareGroupModal] copy link failed:', e);
            toast.error("Couldn't create the link. Try again.");
        } finally {
            setCopying(false);
        }
    };

    const handleSend = async () => {
        if (selectedFriends.length === 0) return;
        setIsSending(true);
        setSendProgress({ current: 0, total: selectedFriends.length });
        let successCount = 0;

        try {
            for (const friendId of selectedFriends) {
                const friend = friends.find(f => String(f.id) === friendId || (f as any).uid === friendId);
                if (!friend) continue;
                const canonicalId = (friend as any)?.uid || String(friend.id);
                setSendProgress(prev => ({ ...prev, current: prev.current + 1 }));

                const newMessage = {
                    id: `${Date.now()}-${Math.random()}`,
                    sender: 'user' as const,
                    text: message || `Shared a ${group.type}: ${group.name}`,
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    timestamp: Date.now(),
                    // Field names match what DirectMessageScreen already reads.
                    // Nulls rather than undefined: Firestore rejects undefined.
                    groupInvite: {
                        id: String(group.id),
                        name: group.name || label,
                        type: group.type,
                        bannerUrl: group.bannerUrl || '',
                        isPublic: group.isPublic !== false,
                    },
                };

                try {
                    await addMessage(canonicalId, newMessage as any);
                    successCount++;
                    // The rules enforce one message per second per sender, so a
                    // multi-friend share has to be paced rather than fired at once.
                    if (selectedFriends.length > 1) await new Promise(r => setTimeout(r, 1100));
                } catch (err: any) {
                    console.error(`[ShareGroupModal] failed to send to ${canonicalId}:`, err?.message || err);
                }
            }

            if (successCount > 0) {
                toast.success(`Shared with ${successCount} friend${successCount !== 1 ? 's' : ''}`);
                onClose();
            } else {
                toast.error('Failed to share with any selected friends.');
            }
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-md bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 text-black dark:text-white p-0 gap-0 overflow-hidden">
                <div className="p-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between bg-[#f2deb3] dark:bg-[#121212]">
                    <h2 className="text-lg font-bold">Share {label}</h2>
                </div>

                {/* Link first: it works for anyone, including people who are not
                    friends on the app yet. */}
                <div className="p-4 border-b border-black/10 dark:border-white/10">
                    <Button
                        variant="outline"
                        onClick={handleCopyLink}
                        disabled={copying}
                        className="w-full justify-start gap-2 rounded-xl border-black/10 dark:border-white/10"
                    >
                        {copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
                        <span className="text-sm">
                            {copying ? 'Creating link...' : copied ? 'Link copied' : `Copy ${label.toLowerCase()} link`}
                        </span>
                        {!copying && !copied && <Copy className="w-3.5 h-3.5 ml-auto opacity-50" />}
                    </Button>
                </div>

                <div className="p-4 border-b border-black/10 dark:border-white/10">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                            placeholder="Search friends..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 bg-black/5 dark:bg-white/5 border-transparent focus:border-[#F39C12] rounded-xl"
                        />
                    </div>
                </div>

                <ScrollArea className="h-64 p-2">
                    {friends.length === 0 ? (
                        <div className="text-center p-8 text-gray-500">
                            <p className="text-sm">No friends yet. Copy the link above to share.</p>
                        </div>
                    ) : filteredFriends.length === 0 ? (
                        <div className="text-center p-8 text-gray-500">
                            No friends found matching "{searchQuery}"
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredFriends.map((friend) => (
                                <div
                                    key={friend.id}
                                    onClick={() => toggleFriendSelection(String(friend.id))}
                                    className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                                        selectedFriends.includes(String(friend.id))
                                            ? 'bg-[#F39C12]/10 dark:bg-[#F39C12]/20'
                                            : 'hover:bg-black/5 dark:hover:bg-white/5'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Avatar className="w-10 h-10 border border-black/10 dark:border-white/10">
                                            <AvatarImage src={(friend as any).image || friend.avatar} />
                                            <AvatarFallback>{friend.name?.[0] || '?'}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-medium text-sm text-black dark:text-white">{friend.name}</p>
                                            <p className="text-xs text-gray-500">@{friend.username?.replace('@', '')}</p>
                                        </div>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                                        selectedFriends.includes(String(friend.id))
                                            ? 'bg-[#F39C12] border-[#F39C12] text-white'
                                            : 'border-gray-400 dark:border-gray-600'
                                    }`}>
                                        {selectedFriends.includes(String(friend.id)) && <Check className="w-3 h-3" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>

                <div className="p-4 border-t border-black/10 dark:border-white/10 bg-[#f9f9f9] dark:bg-[#121212]">
                    <div className="flex flex-col gap-3">
                        <Input
                            placeholder="Write a message (optional)..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 focus:border-[#F39C12] rounded-xl"
                        />
                        <Button
                            onClick={handleSend}
                            disabled={selectedFriends.length === 0 || isSending}
                            className="w-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#4a90e2] hover:from-[#E67E22] hover:to-[#C0392B] dark:hover:from-[#3b6cd4] dark:hover:to-[#357abd] text-white rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSending ? (
                                <span className="flex items-center gap-2">
                                    Sharing ({sendProgress.current}/{sendProgress.total})...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <Send className="w-4 h-4" />
                                    Send to {selectedFriends.length > 0 ? `${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''}` : 'Friends'}
                                </span>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default ShareGroupModal;
